/**
 * Sample mode (W2-02; DESIGN.md §5.6.6, RPE "sampling" X15-01): with Position segments selected, the line's
 * position at each selected segment's start (green) and end (red) is shown with the path between them; dragging a
 * handle writes the segment's start value (`vOut` of its first key) or end value (`vIn` of the next key), in the
 * parent's frame at that time. X/Y link aware: the other axis' key at the same beat (always there on a linked line)
 * is written with it; without one only the segment's own axis moves.
 */

import type { AppServices } from '../../../app/registry/types';
import { Core } from '../../../core/commands/index';
import type { KeyId, LineId } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import type { Sec } from '../../../core/time/types';
import type { MessageRef, Unsubscribe, Vec2 } from '../../../core/util/types';
import type { OverlayDrawContext, OverlayHit, PreviewOverlayProvider } from '../../../render/types';
import { PREVIEW_FRAME_ID } from '../../../render/types';
import type { Gesture, TxnOutcome } from '../../../state/types';
import { NO_FILTERS } from '../filters';
import { m } from '../preview.messages';
import { EXT_SNAP, readExt, snapPrefsOf } from '../prefs';
import { lineStateCopy } from './context';
import {
  lockAxisOf,
  rpePerPx,
  rpeToWorld,
  snapPoint,
  tidyValue,
  worldDeltaToMove,
  worldToRpe,
  type Axis,
} from './gizmoMath';
import { dp, drawLabel } from './shared';
import { overlayTheme } from './theme';

const HANDLE_RADIUS = 7;
const PATH_SAMPLES = 32;
/** The end handle sits just before the next key (the arriving value). */
const END_EPSILON = 1e-6;

export interface SampleSegment {
  readonly line: LineId;
  readonly layer: number;
  /** Keys of the segment start on the X / Y tracks (null when that axis has no key at the beat). */
  readonly startX: KeyId | null;
  readonly startY: KeyId | null;
  /** Keys ending the segment (the next keys), null when that axis has none. */
  readonly endX: KeyId | null;
  readonly endY: KeyId | null;
  readonly t0: Sec;
  readonly t1: Sec;
  /** The segment is a Stay (its end follows its start). */
  readonly hold: boolean;
}

/** Position segments the selection asks to sample (one per line, layer and start beat). */
export function sampleSegments(services: AppServices): SampleSegment[] {
  const { store, engine } = services;
  if (store === null || engine === null) return [];
  const { doc, idx } = store;
  const out: SampleSegment[] = [];
  const seen = new Set<string>();
  const keyAtBeat = (
    line: LineId,
    prop: 'moveX' | 'moveY',
    layer: number,
    t: Readonly<{ w: number; n: number; d: number }>,
  ): KeyId | null => {
    const track = idx.track(line, prop, layer);
    if (track === undefined) return null;
    for (const id of idx.keysByTrack(track)) {
      const k = doc.keys.get(id);
      if (k !== undefined && Beats.eq(k.t, t)) return id;
    }
    return null;
  };
  for (const id of services.editor.getState().selection.segments) {
    const k = doc.keys.get(id);
    if (k === undefined) continue;
    const tr = doc.tracks.get(k.track);
    if (tr?.owner.kind !== 'line' || (tr.owner.prop !== 'moveX' && tr.owner.prop !== 'moveY')) continue;
    const next = idx.nextKey(id);
    if (next === undefined) continue;
    const nk = doc.keys.get(next);
    if (nk === undefined) continue;
    const { line, layer } = tr.owner;
    const tag = `${String(line)}:${String(layer)}:${Beats.key(k.t)}:${Beats.key(nk.t)}`;
    if (seen.has(tag)) continue;
    seen.add(tag);
    const isX = tr.owner.prop === 'moveX';
    const other = isX ? 'moveY' : 'moveX';
    const otherStart = keyAtBeat(line, other, layer, k.t);
    const otherEnd = keyAtBeat(line, other, layer, nk.t);
    out.push({
      line,
      layer,
      startX: isX ? id : otherStart,
      startY: isX ? otherStart : id,
      endX: isX ? next : otherEnd,
      endY: isX ? otherEnd : next,
      t0: engine.tempo.secAt(k.t),
      t1: engine.tempo.secAt(nk.t),
      hold: k.ease.kind === 'hold',
    });
  }
  return out;
}

interface Handle {
  readonly seg: SampleSegment;
  readonly end: boolean;
  readonly px: Vec2;
}

interface Drag {
  readonly handle: Handle;
  readonly startPx: Vec2;
  readonly startWorld: Vec2;
  readonly posWorld: Vec2;
  readonly parentRot: number;
  readonly ar: number;
  readonly v0x: number | null;
  readonly v0y: number | null;
  readonly gesture: Gesture;
  lock: Axis | null;
  last: TxnOutcome<void> | null;
  offEsc: Unsubscribe | null;
}

function numberValue(services: AppServices, key: KeyId | null, end: boolean): number | null {
  if (key === null) return null;
  const k = services.store?.doc.keys.get(key);
  if (k === undefined) return null;
  const v = end ? k.vIn : k.vOut;
  return typeof v === 'number' ? v : null;
}

export function createSampleModeOverlay(services: AppServices): PreviewOverlayProvider {
  let handles: Handle[] = [];
  let drag: Drag | null = null;

  const enabled = (): boolean => services.editor.getState().preview.sampleMode;

  const finish = (): void => {
    drag?.offEsc?.();
    drag = null;
    services.ui.setGestureHints(null);
    services.scheduler.request(PREVIEW_FRAME_ID);
  };

  const release = (): void => {
    const d = drag;
    if (d === null) return;
    if (d.last === null) d.gesture.cancel();
    else if (!d.last.ok) {
      d.gesture.cancel();
      services.ui.toast(d.last.reason, { kind: 'error' });
    } else d.gesture.commit();
    finish();
  };

  const handleAt = (c: OverlayDrawContext, px: number, py: number): Handle | null => {
    const r = dp(c, HANDLE_RADIUS + 3);
    for (let i = handles.length - 1; i >= 0; i--) {
      const h = handles[i];
      if (h !== undefined && Math.hypot(px - h.px[0], py - h.px[1]) <= r) return h;
    }
    return null;
  };

  return {
    id: 'preview.sampleMode',
    order: 80,
    draw(c) {
      handles = [];
      if (!enabled() || services.engine === null) return;
      const engine = services.engine;
      const ar = c.viewport.ar;
      const theme = overlayTheme();
      const ctx = c.ctx;
      for (const seg of sampleSegments(services)) {
        const path: Vec2[] = [];
        for (let i = 0; i <= PATH_SAMPLES; i++) {
          const t = seg.t0 + ((seg.t1 - END_EPSILON - seg.t0) * i) / PATH_SAMPLES;
          const s = lineStateCopy(engine, seg.line, t, NO_FILTERS, ar);
          path.push(c.worldToPx(s.x, s.y));
        }
        const a = path[0];
        const b = path[path.length - 1];
        if (a === undefined || b === undefined) continue;
        ctx.save();
        ctx.strokeStyle = theme.guide;
        ctx.lineWidth = dp(c, 1.5);
        ctx.setLineDash([dp(c, 4), dp(c, 3)]);
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        for (const p of path) ctx.lineTo(p[0], p[1]);
        ctx.stroke();
        ctx.setLineDash([]);
        for (const [p, color, end] of [
          [a, theme.success, false],
          [b, theme.danger, true],
        ] as const) {
          ctx.beginPath();
          ctx.arc(p[0], p[1], dp(c, HANDLE_RADIUS), 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.globalAlpha = end && seg.hold ? 0.45 : 0.9;
          ctx.fill();
          ctx.globalAlpha = 1;
          ctx.strokeStyle = theme.surface;
          ctx.lineWidth = dp(c, 1.5);
          ctx.stroke();
          handles.push({ seg, end, px: p });
        }
        ctx.restore();
        drawLabel(c, services.i18n.t(m.sampleStart), a[0] + dp(c, 10), a[1] + dp(c, 6), {
          fg: theme.text,
          bg: theme.surface,
          border: theme.success,
          size: 10,
        });
        drawLabel(c, services.i18n.t(m.sampleEnd), b[0] + dp(c, 10), b[1] + dp(c, 6), {
          fg: theme.text,
          bg: theme.surface,
          border: theme.danger,
          size: 10,
        });
      }
    },
    hitTest(c, px, py): OverlayHit | null {
      if (!enabled()) return null;
      const h = handleAt(c, px, py);
      if (h === null) return null;
      if (h.end && h.seg.hold) {
        return {
          provider: 'preview.sampleMode',
          cursor: 'not-allowed',
          data: { handle: h, tooltip: { key: m.sampleHoldEnd } },
        };
      }
      return {
        provider: 'preview.sampleMode',
        cursor: 'move',
        data: { handle: h, tooltip: { key: h.end ? m.sampleEndHelp : m.sampleStartHelp } },
      };
    },
    onPointerDown(c, hit, e) {
      const engine = services.engine;
      const store = services.store;
      if ((e.buttons & 1) === 0 || engine === null || store === null) return;
      const h = (hit.data as { readonly handle?: Handle } | null)?.handle;
      if (h === undefined || (h.end && h.seg.hold)) return;
      if (services.editor.getState().filters.lockedLines.has(h.seg.line)) {
        services.ui.toast({ key: m.lineLocked }, { kind: 'warning' });
        return;
      }
      const ar = c.viewport.ar;
      const t = h.end ? h.seg.t1 - END_EPSILON : h.seg.t0;
      const s = lineStateCopy(engine, h.seg.line, t, NO_FILTERS, ar);
      const parentId = store.doc.lines.get(h.seg.line)?.parent ?? null;
      const parentRot = parentId === null ? 0 : lineStateCopy(engine, parentId, t, NO_FILTERS, ar).rot;
      const gesture = store.beginGesture({ label: { key: m.txnSample }, command: 'preview.sample' });
      const onKey = (ev: KeyboardEvent): void => {
        if (ev.key !== 'Escape' || drag === null) return;
        ev.preventDefault();
        ev.stopImmediatePropagation();
        drag.gesture.cancel();
        finish();
      };
      if (typeof window !== 'undefined') window.addEventListener('keydown', onKey, true);
      drag = {
        handle: h,
        startPx: [e.px, e.py],
        startWorld: e.world,
        posWorld: [s.x, s.y],
        parentRot,
        ar,
        v0x: numberValue(services, h.end ? h.seg.endX : h.seg.startX, h.end),
        v0y: numberValue(services, h.end ? h.seg.endY : h.seg.startY, h.end),
        gesture,
        lock: null,
        last: null,
        offEsc: () => {
          if (typeof window !== 'undefined') window.removeEventListener('keydown', onKey, true);
        },
      };
      const items: Array<{ chord: string; label: MessageRef }> = [
        { chord: '', label: { key: h.end ? m.sampleEndHelp : m.sampleStartHelp } },
        { chord: 'Shift', label: { key: m.hintLock } },
        { chord: 'Alt', label: { key: m.hintNoSnap } },
        { chord: 'Escape', label: { key: m.hintCancel } },
      ];
      services.ui.setGestureHints(items);
    },
    onPointerMove(c, e) {
      const d = drag;
      if (d === null) return;
      if ((e.buttons & 1) === 0) {
        release();
        return;
      }
      const [wx, wy] = c.pxToWorld(e.px, e.py);
      let tx = d.posWorld[0] + wx - d.startWorld[0];
      let ty = d.posWorld[1] + wy - d.startWorld[1];
      if (!e.alt) {
        const prefs = readExt(services.settings, EXT_SNAP, snapPrefsOf);
        const [rx, ry] = rpePerPx(c.viewport.W, d.ar);
        const thr = dp(c, 8);
        const [X, Y] = worldToRpe(tx, ty, d.ar);
        const r = snapPoint(X, Y, {
          grid: prefs.grid ? prefs.gridSize : 0,
          centre: prefs.centre,
          edges: prefs.edges,
          anchors: [],
          thresholdX: thr * rx,
          thresholdY: thr * ry,
        });
        [tx, ty] = rpeToWorld(r.x, r.y, d.ar);
      }
      const [dX, dY] = worldDeltaToMove(tx - d.posWorld[0], ty - d.posWorld[1], d.parentRot, d.ar);
      if (e.shift) {
        // Decided on the pointer's own travel in world units (isotropic), not on the snapped RPE delta.
        if (d.lock === null && Math.hypot(e.px - d.startPx[0], e.py - d.startPx[1]) >= dp(c, 4))
          d.lock = lockAxisOf(wx - d.startWorld[0], wy - d.startWorld[1], d.parentRot);
      } else d.lock = null;
      const seg = d.handle.seg;
      const end = d.handle.end;
      const kx = end ? seg.endX : seg.startX;
      const ky = end ? seg.endY : seg.startY;
      const part = end ? 'in' : 'out';
      const writeX = kx !== null && d.v0x !== null && !(e.shift && d.lock === 'y');
      const writeY = ky !== null && d.v0y !== null && !(e.shift && d.lock === 'x');
      d.last = d.gesture.update((txb) => {
        if (writeX) {
          Core.keys.setKeyValues(txb, { keys: [kx], part, value: tidyValue(d.v0x + dX) });
        }
        if (writeY) {
          Core.keys.setKeyValues(txb, { keys: [ky], part, value: tidyValue(d.v0y + dY) });
        }
      });
      services.scheduler.request(PREVIEW_FRAME_ID);
    },
    onPointerUp() {
      release();
    },
  };
}
